// CalendarPopup.jsx
import React from 'react';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CalendarPopup = ({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  selectedDueDate,
  selectedStartDate,
  selectedEndDate,
  tempStartDate,
  tempEndDate,
  calendarMode,
  onToggleMode,
  onDateSelect,
  onSave,
}) => {
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Previous month days
    for (let i = startingDay - 1; i >= 0; i--) {
      const prevMonthDate = new Date(year, month - 1, daysInPrevMonth - i);
      prevMonthDate.setHours(0, 0, 0, 0);
      const isSelected = calendarMode === 'dueDate'
        ? selectedDueDate && new Date(selectedDueDate).toDateString() === prevMonthDate.toDateString()
        : (tempStartDate && tempStartDate.toDateString() === prevMonthDate.toDateString()) ||
          (tempEndDate && tempEndDate.toDateString() === prevMonthDate.toDateString());
      const isInRange = tempStartDate && tempEndDate &&
        prevMonthDate >= tempStartDate && prevMonthDate <= tempEndDate;

      days.push(
        <div
          key={`prev-${i}`}
          className={`calendar-day other-month ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''}`}
          onClick={() => onDateSelect(prevMonthDate)}
          style={{ backgroundColor: isInRange ? '#e3f2fd' : 'transparent' }}
        >
          {daysInPrevMonth - i}
        </div>
      );
    }

    // Current month days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      const isSelected = calendarMode === 'dueDate'
        ? selectedDueDate && new Date(selectedDueDate).toDateString() === date.toDateString()
        : (tempStartDate && tempStartDate.toDateString() === date.toDateString()) ||
          (tempEndDate && tempEndDate.toDateString() === date.toDateString());
      const isInRange = tempStartDate && tempEndDate &&
        date >= tempStartDate && date <= tempEndDate;
      const isToday = date.toDateString() === today.toDateString();
      const isStart = tempStartDate && tempStartDate.toDateString() === date.toDateString();
      const isEnd = tempEndDate && tempEndDate.toDateString() === date.toDateString();

      days.push(
        <div
          key={d}
          className={`calendar-day current-month ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isStart ? 'range-start' : ''} ${isEnd ? 'range-end' : ''}`}
          onClick={() => onDateSelect(date)}
          style={{
            backgroundColor: isInRange ? '#e3f2fd' : 'transparent',
            border: (isStart || isEnd) ? '2px solid #1976d2' : 'none',
          }}
        >
          {d}
        </div>
      );
    }
    return days;
  };

  return (
    <div style={{ padding: '12px' }}>
      {/* Mode Toggle */}
      <div style={{ display: 'flex', marginBottom: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', padding: '2px' }}>
        <button
          onClick={() => onToggleMode('dueDate')}
          style={{
            flex: 1, padding: '6px', border: 'none', borderRadius: '4px',
            backgroundColor: calendarMode === 'dueDate' ? '#1976d2' : 'transparent',
            color: calendarMode === 'dueDate' ? 'white' : '#666', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
          }}
        >
          Due Date
        </button>
        <button
          onClick={() => onToggleMode('duration')}
          style={{
            flex: 1, padding: '6px', border: 'none', borderRadius: '4px',
            backgroundColor: calendarMode === 'duration' ? '#1976d2' : 'transparent',
            color: calendarMode === 'duration' ? 'white' : '#666', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
          }}
        >
          Duration
        </button>
      </div>

      {/* Selection Info */}
      <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '12px' }}>
        {calendarMode === 'dueDate' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-calendar-day" style={{ color: '#1976d2' }} />
            <span>
              {selectedDueDate ? `Due Date: ${new Date(selectedDueDate).toLocaleDateString()}` : 'No due date set'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-play" style={{ color: '#28a745', fontSize: '10px' }} />
              <span>
                Start: {tempStartDate ? tempStartDate.toLocaleDateString() :
                  (selectedStartDate ? new Date(selectedStartDate).toLocaleDateString() : 'Not selected')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-stop" style={{ color: '#dc3545', fontSize: '10px' }} />
              <span>
                End: {tempEndDate ? tempEndDate.toLocaleDateString() :
                  (selectedEndDate ? new Date(selectedEndDate).toLocaleDateString() : 'Not selected')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Calendar Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <button onClick={onPrevMonth} style={{ background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}>
          <i className="fas fa-chevron-left" />
        </button>
        <span style={{ fontWeight: 'bold' }}>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
        <button onClick={onNextMonth} style={{ background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}>
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      {/* Day Labels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '5px' }}>
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div key={day} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#666' }}>{day}</div>
        ))}
      </div>

      {/* Calendar Days */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '12px' }}>
        {generateCalendarDays()}
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '4px' }}>
        <button
          onClick={onSave}
          disabled={calendarMode === 'duration' && (!tempStartDate || !tempEndDate)}
          style={{
            padding: '6px 16px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px',
            fontSize: '12px', fontWeight: 500, cursor: (calendarMode === 'duration' && (!tempStartDate || !tempEndDate)) ? 'not-allowed' : 'pointer',
            opacity: (calendarMode === 'duration' && (!tempStartDate || !tempEndDate)) ? 0.5 : 1,
          }}
        >
          Save
        </button>
      </div>

      <style>{`
        .calendar-day { text-align: center; padding: 6px 2px; font-size: 12px; cursor: pointer; border-radius: 4px; transition: all 0.2s ease; }
        .calendar-day:hover:not(.disabled) { background-color: #e3f2fd !important; }
        .calendar-day.selected { background-color: #1976d2 !important; color: white !important; }
        .calendar-day.today { border: 1px solid #1976d2; }
        .calendar-day.other-month { color: #999; }
        .calendar-day.in-range { background-color: #e3f2fd; }
        .calendar-day.range-start, .calendar-day.range-end { background-color: #1976d2 !important; color: white !important; }
      `}</style>
    </div>
  );
};

export default CalendarPopup;